import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  RefreshCw,
  AlertTriangle,
  Mail,
  DollarSign,
  BarChart3,
  Download,
  Brain,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { SentimentLayout } from "./layout";
import { ComplaintTable } from "@/components/sentiment/ComplaintTable";
import { TicketDrilldown } from "@/components/sentiment/TicketDrilldown";
import {
  fetchSentimentDashboard,
  fetchSentimentComplaints,
  triggerSentimentExtraction,
  triggerSentimentAnalysis,
} from "@/lib/api";

function StatCard({
  label,
  value,
  icon: Icon,
  subValue,
  loading,
}: {
  label: string;
  value: string | number;
  icon: any;
  subValue?: string;
  loading?: boolean;
}) {
  return (
    <div className="bg-white rounded-lg border p-4 flex items-start gap-3">
      <div className="p-2 bg-gray-100 rounded-lg">
        <Icon className="h-5 w-5 text-gray-600" />
      </div>
      <div>
        {loading ? (
          <Skeleton className="h-7 w-20" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
        <div className="text-sm text-gray-500">{label}</div>
        {subValue && <div className="text-xs text-gray-400 mt-0.5">{subValue}</div>}
      </div>
    </div>
  );
}

function SeverityDistribution({
  data,
  loading,
}: {
  data: { severity: number; count: number }[];
  loading: boolean;
}) {
  if (loading) return <Skeleton className="h-32 w-full" />;
  if (!data || data.length === 0) return <div className="text-sm text-gray-400">No data</div>;

  const maxCount = Math.max(...data.map((d) => d.count));
  const colors: Record<number, string> = {
    1: "bg-green-400",
    2: "bg-yellow-400",
    3: "bg-orange-400",
    4: "bg-red-400",
    5: "bg-red-600",
  };

  return (
    <div className="space-y-2">
      {[5, 4, 3, 2, 1].map((sev) => {
        const item = data.find((d) => d.severity === sev);
        const count = item?.count || 0;
        const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
        return (
          <div key={sev} className="flex items-center gap-2">
            <span className="text-xs w-6 text-right">{sev}</span>
            <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
              <div className={`h-full rounded-full ${colors[sev]}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-xs w-8 text-gray-500">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

function TopCategories({
  data,
  loading,
}: {
  data: { category: string; count: number }[];
  loading: boolean;
}) {
  if (loading) return <Skeleton className="h-32 w-full" />;
  if (!data || data.length === 0) return <div className="text-sm text-gray-400">No data</div>;

  return (
    <div className="space-y-1.5">
      {data.slice(0, 8).map((item) => (
        <div key={item.category} className="flex items-center justify-between">
          <Badge variant="outline" className="text-xs">
            {item.category.replace("_", " ")}
          </Badge>
          <span className="text-sm font-medium">{item.count}</span>
        </div>
      ))}
    </div>
  );
}

export default function SentimentDashboardPage() {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const dashboardQuery = useQuery({
    queryKey: ["sentimentDashboard"],
    queryFn: () => fetchSentimentDashboard(),
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const complaintsQuery = useQuery({
    queryKey: ["sentimentComplaints", page, severityFilter, search],
    queryFn: () =>
      fetchSentimentComplaints({
        page,
        pageSize: 25,
        severity: severityFilter !== "all" ? parseInt(severityFilter) : undefined,
        search: search || undefined,
      }),
    refetchInterval: autoRefresh ? 30000 : false,
  });

  const extractMutation = useMutation({
    mutationFn: triggerSentimentExtraction,
    onSuccess: (data) => {
      toast({ title: "Extraction complete", description: `Results: ${JSON.stringify(data.results?.length || 0)} pipeline(s) processed` });
      dashboardQuery.refetch();
      complaintsQuery.refetch();
    },
    onError: (err: any) => {
      toast({ title: "Extraction failed", description: err.message, variant: "destructive" });
    },
  });

  const analyseMutation = useMutation({
    mutationFn: triggerSentimentAnalysis,
    onSuccess: (data) => {
      toast({ title: "Analysis queued", description: `${data.emailsQueued} emails queued for analysis` });
      dashboardQuery.refetch();
    },
    onError: (err: any) => {
      toast({ title: "Analysis failed", description: err.message, variant: "destructive" });
    },
  });

  const stats = dashboardQuery.data;
  const complaints = complaintsQuery.data;
  const loading = dashboardQuery.isLoading;

  const handleTicketClick = (ticketId: string) => {
    setSelectedTicketId(ticketId);
    setDrilldownOpen(true);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "Never";
    try {
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(dateString));
    } catch {
      return "-";
    }
  };

  return (
    <SentimentLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Sentiment Analysis</h1>
            <p className="text-sm text-gray-500 mt-1">
              HubSpot Production pipeline email complaint detection
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-500">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded"
              />
              Auto-refresh
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => extractMutation.mutate()}
              disabled={extractMutation.isPending}
            >
              {extractMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Download className="h-4 w-4 mr-1" />
              )}
              Extract
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => analyseMutation.mutate()}
              disabled={analyseMutation.isPending}
            >
              {analyseMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Brain className="h-4 w-4 mr-1" />
              )}
              Analyse
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                dashboardQuery.refetch();
                complaintsQuery.refetch();
              }}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Tickets Tracked"
            value={stats?.totalTickets || 0}
            icon={Mail}
            subValue={`${stats?.totalEmails || 0} emails`}
            loading={loading}
          />
          <StatCard
            label="Complaints Found"
            value={stats?.complaintsFound || 0}
            icon={AlertTriangle}
            subValue={`${((stats?.complaintRate || 0) * 100).toFixed(1)}% of analysed`}
            loading={loading}
          />
          <StatCard
            label="Analysed"
            value={stats?.analysedEmails || 0}
            icon={BarChart3}
            subValue={`Last: ${formatDate(stats?.lastAnalysisAt)}`}
            loading={loading}
          />
          <StatCard
            label="Total Cost"
            value={`$${(stats?.totalCostUsd || 0).toFixed(4)}`}
            icon={DollarSign}
            subValue={`Last extraction: ${formatDate(stats?.lastExtractionAt)}`}
            loading={loading}
          />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <h3 className="text-sm font-medium mb-3">Severity Distribution</h3>
            <SeverityDistribution
              data={stats?.severityDistribution || []}
              loading={loading}
            />
          </div>
          <div className="bg-white rounded-lg border p-4">
            <h3 className="text-sm font-medium mb-3">Top Categories</h3>
            <TopCategories
              data={stats?.topCategories || []}
              loading={loading}
            />
          </div>
        </div>

        {/* Complaint Table */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b flex items-center gap-4">
            <h3 className="text-sm font-medium">Complaints</h3>
            <Select value={severityFilter} onValueChange={(v) => { setSeverityFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                <SelectItem value="5">Severity 5</SelectItem>
                <SelectItem value="4">Severity 4</SelectItem>
                <SelectItem value="3">Severity 3</SelectItem>
                <SelectItem value="2">Severity 2</SelectItem>
                <SelectItem value="1">Severity 1</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="Search tickets, titles, summaries..."
              className="max-w-sm h-8"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
            {complaints && (
              <span className="text-xs text-gray-500 ml-auto">
                {complaints.total} total
              </span>
            )}
          </div>

          {complaintsQuery.isLoading ? (
            <div className="p-8 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <ComplaintTable
                complaints={complaints?.complaints || []}
                onTicketClick={handleTicketClick}
              />
              {complaints && complaints.totalPages > 1 && (
                <div className="p-3 border-t flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-500">
                    Page {complaints.page} of {complaints.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= complaints.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <TicketDrilldown
        ticketId={selectedTicketId}
        open={drilldownOpen}
        onOpenChange={setDrilldownOpen}
      />
    </SentimentLayout>
  );
}
